import { Box, Paper, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Sankey, { rootNodeBracket } from "../../components/chart/Sankey";
import "../../style/components-style/SankeyDiagram.css";
import { scope1Categories, scope2Categories, scope3Categories } from "./ScopeCategory";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

const SankeyDiagram = ({ emissions = [], emission_details = [] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tok = getOrgTokens(isDark);
  const localEmissionsDetails = useMemo(() => {
    if (!emission_details) return [];
    const emissionIdSet = new Set(emissions.map((e) => e.id));
    return emission_details.filter((d) => emissionIdSet.has(d.emission_id));
  }, [emissions, emission_details]);
  const sankeyData = useMemo(() => {
    if (!emissions || emissions.length < 1) {
      return { nodes: [], links: [] };
    }
    const rootLinksWeight = [0, 0, 0];
    const categoryBracket = "__Category__";
    const typeBracket = "__Type__";
    
    const includesCategory = (categories, cat) => {
      if (!cat) return false;
      const normalized = cat.toLowerCase().trim();
      return categories.some(c => c.toLowerCase().trim() === normalized);
    };

    const emissionLinks = emissions
      .filter((e) => (Number(e.gwp) || Number(e.gwp_total) || 0) > 0)
      .map((e) => {
        let source = rootNodeBracket + "0" + rootNodeBracket;
        const value = Number(e.gwp) || Number(e.gwp_total) || 0;
        if (includesCategory(scope1Categories, e.category)) {
          source = rootNodeBracket + "1" + rootNodeBracket;
          rootLinksWeight[0] += value;
        } else if (includesCategory(scope2Categories, e.category)) {
          source = rootNodeBracket + "2" + rootNodeBracket;
          rootLinksWeight[1] += value;
        } else if (includesCategory(scope3Categories, e.category)) {
          source = rootNodeBracket + "3" + rootNodeBracket;
          rootLinksWeight[2] += value;
        }
        const target = categoryBracket + (e.category || "NULL") + categoryBracket;
        return { source, target, value };
      });
    const detailLinks = localEmissionsDetails
      .filter((e) => e.totalco2eq > 0)
      .map((e) => {
        const sourceCategory = emissions.find((em) => em.id === e.emission_id)?.category;
        const source = categoryBracket + (sourceCategory || "NULL") + categoryBracket;
        const target = typeBracket + (e.type || "NULL") + typeBracket;
        const value = e.totalco2eq;
        return { source, target, value };
      });
    const rootLinks = rootLinksWeight.reduce((acc, r, i) => {
      if (r !== 0) {
        acc.push({ source: rootNodeBracket + "0" + rootNodeBracket, target: rootNodeBracket + (i + 1).toString() + rootNodeBracket, value: r });
      }
      return acc;
    }, []);
    const links = [...rootLinks, ...emissionLinks, ...detailLinks];
    const rootNodes = [{ id: rootNodeBracket + "0" + rootNodeBracket, label: "Scope 1 - 3" }];
    if (rootLinksWeight[0] > 0) rootNodes.push({ id: rootNodeBracket + "1" + rootNodeBracket, label: "Scope 1" });
    if (rootLinksWeight[1] > 0) rootNodes.push({ id: rootNodeBracket + "2" + rootNodeBracket, label: "Scope 2" });
    if (rootLinksWeight[2] > 0) rootNodes.push({ id: rootNodeBracket + "3" + rootNodeBracket, label: "Scope 3" });
    const emissionsNodes = [...new Set(emissions.map((e) => e.category))].map((category) => ({
      id: categoryBracket + (category || "NULL") + categoryBracket,
      label: category || "NULL",
    }));
    const detailsNodes = [...new Set(localEmissionsDetails.map((e) => e.type))].map((type) => ({ id: typeBracket + (type || "NULL") + typeBracket, label: type || "NULL" }));
    const nodes = [...rootNodes, ...emissionsNodes, ...detailsNodes];
    const newData = { nodes, links };
    return newData;
  }, [emissions, localEmissionsDetails]);
  return (
    <Paper
      variant="Chart Container"
      sx={{
        p: ORG_PALETTE.spacing.chartPad,
        borderRadius: ORG_PALETTE.radius.card,
        border: "1px solid",
        borderColor: tok.border.card,
        bgcolor: tok.bg.card,
        overflow: "hidden"
      }}
    >
      <Typography variant="h6" sx={{ mb: 4, pb: 2, fontWeight: 500, color: tok.text.chartTitle, borderBottom: `1px solid ${tok.border.divider}` }}>
        {t("Sankey Breakdown")}
      </Typography>
      <Box sx={{ overflow: "hidden" }}>
        {sankeyData.links.length > 0 && (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", paddingX: 4 }}>
              <Typography sx={{ fontFamily: "Poppins", color: tok.text.primary }}></Typography>
              <Typography sx={{ fontFamily: "Poppins", color: tok.text.primary }}>{t("Scope")}</Typography>
              <Typography sx={{ fontFamily: "Poppins", color: tok.text.primary }}>{t("Category")}</Typography>
              <Typography sx={{ fontFamily: "Poppins", color: tok.text.primary }}>{t("Type")}</Typography>
            </Box>
            <Sankey data={sankeyData} />
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default SankeyDiagram;
